import React from 'react';
import { Calendar, Heart } from 'lucide-react';
import { urlFor, getImageAlt } from '../../lib/sanity';
import type { Pet } from '../../types/sanity';

interface DeceasedPetCardProps {
  pet: Pet;
  calculateAge: (birthDate: string) => string;
}

export function DeceasedPetCard({ pet, calculateAge }: DeceasedPetCardProps) {
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Data nieznana';
    
    try {
      return new Date(dateString).toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Data nieznana';
    }
  };

  return (
    <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black rounded-lg shadow-lg overflow-hidden flex flex-col h-full group">
      <div className="relative aspect-video overflow-hidden">
        <img
          src={urlFor(pet.image)}
          alt={getImageAlt(pet.image, pet) || `${pet.name} - zwierzak, który odszedł za tęczowy most`}
          className="w-full h-full object-cover object-center grayscale transition-all duration-300 group-hover:grayscale-0"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-4 left-4">
          <h3 className="heading-4 text-white text-shadow-md mb-0">
            {pet.name}
          </h3>
        </div>
      </div>
      
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-4">
          <Heart className="w-5 h-5 text-accessible-red-600" aria-hidden="true" />
          <span className="body-small text-accessible-gray-700 dark:text-white high-contrast:text-white no-margin">
            {pet.mainCategory === 'dog' ? 'Piesek' : 'Kotek'}
          </span>
        </div>
        
        <div className="space-y-4 mb-6 flex-1">
          {pet.deceasedDescription ? (
            <p className="body-medium">
              {pet.deceasedDescription || "Wspomnienie o tym zwierzaku nie zostało jeszcze dodane."}
            </p>
          ) : (
            <p className="body-medium">
              {pet.description || "Opis tego zwierzaka nie jest dostępny."}
            </p>
          )}
        </div>
        
        <div className="pt-4 border-t border-accessible-gray-200 dark:border-accessible-gray-700 high-contrast:border-white/20">
          <div className="grid grid-cols-2 gap-4">
            <div className="overflow-hidden">
              <p className="label-small text-accessible-gray-700 dark:text-white high-contrast:text-white mb-1">
                Data urodzenia
              </p>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-accessible-gray-600 dark:text-white/90 high-contrast:text-white" />
                <p className="body-small text-accessible-gray-700 dark:text-white high-contrast:text-white no-margin">
                  {formatDate(pet.birthDate)}
                </p>
              </div>
            </div>
            <div className="overflow-hidden">
              <p className="label-small text-accessible-gray-700 dark:text-white high-contrast:text-white mb-1">
                Data odejścia
              </p>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-accessible-gray-600 dark:text-white/90 high-contrast:text-white" />
                <p className="body-small text-accessible-gray-700 dark:text-white high-contrast:text-white no-margin">
                  {formatDate(pet.deceasedDate)}
                </p>
              </div>
            </div>
          </div>
          <p className="body-small text-accessible-gray-700 dark:text-white high-contrast:text-white mt-2">
            Wiek: {calculateAge(pet.birthDate)}
          </p>
        </div>
      </div>
    </div>
  );
}